import * as React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import {
  ContentContextProvider,
  FootnoteContextProvider,
  fetchCaeEnforcement,
  type GfaServiceConfigLike,
  type AcsResult,
} from '@oneaudi/feature-app-utils';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { AudiVideoServiceInterfaceV1 } from '@oneaudi/video-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { Logger } from '@feature-hub/logger';
import { BasicTeaserContentLoader } from './components/BasicTeaser';
import type { BasicTeaserContent } from '../types';
import { getDesignLibrary } from './utils/alpha-helper';
import type { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import type { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import I18nContextComponent from '@oneaudi/i18n-context';

interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'audi-tracking-service': TrackingServiceV2;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-video-service': AudiVideoServiceInterfaceV1;
  readonly 'audi-render-mode-service': RenderModeServiceV1;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'onegraph-service': OneGraphServiceV1;
  readonly 'gfa:layer-manager'?: GfaLayerManagerV1;
  readonly 'layer-manager': LayerManagerV25;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'gfa:service-config-provider'?: GfaServiceConfigLike;
}

export default {
  dependencies: {
    externals: {
      react: '*',
      'styled-components': '^5',
      '@feature-hub/react': '^3',
      ...getDesignLibrary(),
    },
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'locale-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
      'layer-manager': '^2.5.0',
      'dbad:audi-i18n-service': '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'audi-video-service': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'gfa:layer-manager': '1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
  },
  create: ({
    featureServices: {
      'audi-content-service': contentService,
      'locale-service': localeService,
      'audi-footnote-reference-service': footnoteReferenceServiceScopeManager,
      'audi-tracking-service': trackingService,
      'audi-render-mode-service': renderModeService,
      's2:async-ssr-manager': asyncSSRManager,
      's2:serialized-state-manager': serializedStateManager,
      'onegraph-service': oneGraphService,
      's2:logger': loggerService,
      'gfa:layer-manager': layerManager,
      'dbad:audi-i18n-service': i18nService,
      'gfa:service-config-provider': gfaServiceConfigProvider,
    },
    config,
    featureAppId: id,
  }: FeatureAppEnvironment<FeatureServiceDependencies, BasicTeaserContent>) => {
    if (trackingService) {
      trackingService.featureAppName = 'basic teaser';
    }
    const { OneGraphProvider } = oneGraphService;
    const requiredServiceForOneGraph = {
      'onegraph-service': oneGraphService,
      'locale-service': localeService,
      's2:async-ssr-manager': asyncSSRManager,
    };

    const requiredServicesI18n = {
      'dbad:audi-i18n-service': i18nService,
      'locale-service': localeService,
      's2:async-ssr-manager': asyncSSRManager,
    };

    const footnoteReferenceService =
      footnoteReferenceServiceScopeManager?.getDefaultScopeRefService();

    const contentContextService = contentService as ContentServiceV1 & {
      isInInitialViewport?: () => boolean;
    };

    if (typeof contentContextService.isInInitialViewport !== 'function') {
      contentContextService.isInInitialViewport = () => false;
    }

    // Get all carline IDs from content — prefer normalized content service (handles AEM headless),
    // fall back to editor.json config prop
    const rawFromService = contentService.getContent() as BasicTeaserContent & {
      __type?: string;
      fields?: { consumptionAndEmissions?: string[] };
    };
    const idsFromService =
      rawFromService?.__type === 'aem-headless'
        ? (rawFromService?.fields?.consumptionAndEmissions ?? [])
        : (rawFromService?.consumptionAndEmissions ?? []);
    const rawConfig = config as BasicTeaserContent;
    const carlineIds = (
      idsFromService.length > 0 ? idsFromService : (rawConfig?.consumptionAndEmissions ?? [])
    ).filter(Boolean);

    // SSR state management
    let ssrState: { acsResult?: AcsResult } | undefined;

    if (asyncSSRManager) {
      const loadingPromise = (async () => {
        let acsResult: { caeDataRequired: boolean };
        if (carlineIds.length > 0 && gfaServiceConfigProvider) {
          try {
            acsResult = await fetchCaeEnforcement({ gfaServiceConfigProvider });
          } catch (error) {
            loggerService?.warn('ACS fetch failed during SSR, defaulting to no enforcement', error);
            acsResult = { caeDataRequired: false };
          }
        } else {
          acsResult = { caeDataRequired: false };
        }
        ssrState = { acsResult };
        return ssrState;
      })();

      asyncSSRManager.scheduleRerender(loadingPromise);
      serializedStateManager?.register(() => JSON.stringify(ssrState));
    } else {
      // CSR hydration: deserialize SSR state
      const serializedData = serializedStateManager?.getSerializedState();
      if (serializedData) {
        try {
          ssrState = JSON.parse(serializedData) as { acsResult?: AcsResult };
        } catch (error) {
          loggerService?.warn('Failed to parse serialized state, starting fresh', error);
          ssrState = undefined;
        }
      } else {
        ssrState = undefined;
      }
    }

    return {
      render: () => (
        <ContentContextProvider contentService={contentContextService}>
          <OneGraphProvider featureServices={requiredServiceForOneGraph}>
            <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
              <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                <UniversalEditorProvider
                  contentService={contentService}
                  renderModeService={renderModeService}
                >
                  <I18nContextComponent
                    i18nData={i18nService}
                    language={localeService.language}
                    featureServices={requiredServicesI18n}
                    scopes={[]}
                  >
                    <BasicTeaserContentLoader
                      oneGraphService={oneGraphService}
                      loggerService={loggerService}
                      localeService={localeService}
                      trackingService={trackingService}
                      initialContent={config}
                      meta={{ id }}
                      layerManager={layerManager as LayerManagerV25}
                      acsResult={ssrState?.acsResult}
                      gfaServiceConfigProvider={gfaServiceConfigProvider}
                    />
                  </I18nContextComponent>
                </UniversalEditorProvider>
              </div>
            </FootnoteContextProvider>
          </OneGraphProvider>
        </ContentContextProvider>
      ),
    };
  },
} as FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies>;
